import { useState } from "react";

import type { FocusEvent } from "react";

/** Whether focus at `target` belongs to `row` itself, not to a treeitem nested in it */
function isOwnFocus(target: EventTarget | null, row: HTMLElement) {
  return (
    target instanceof Element &&
    (target.closest('[role="treeitem"]') === row || !!target.closest('[role="menu"]'))
  );
}

/**
 * Tracks whether focus is on a sidebar row itself or its controls, so the row mounts its menu button
 * the same way it does on hover. The button shows itself only on hover or keyboard focus (CSS in
 * ContextMenu), so a row focused by a click keeps its hover-only look. Focus inside an open menu still
 * counts: the menu is portaled, and closing it hands focus back to the menu button, which must still
 * exist. Focus in a child treeitem doesn't count, so a folder's controls leave the Tab order when focus
 * moves into its contents.
 */
export function useRowFocusWithin() {
  const [focusWithin, setFocusWithin] = useState(false);

  const focusWithinProps = {
    onFocus: (event: FocusEvent<HTMLElement>) => {
      if (isOwnFocus(event.target, event.currentTarget)) setFocusWithin(true);
    },
    onBlur: (event: FocusEvent<HTMLElement>) => {
      if (!isOwnFocus(event.relatedTarget, event.currentTarget)) setFocusWithin(false);
    },
  };

  return { focusWithin, focusWithinProps };
}
